import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useUser, useUserContext } from '../../../../context/UserProvider'
import { fetchCarByAccountNo } from '../../../../api/api'
import { ExcelExportButton } from '../components/ExcelExporter'
import FleetReportExpenseTable from '../components/FleetReportExpenseTable'
import type { FleetReportExpense as FleetReportExpenseType, Car } from '../../../../types'
import { excelFleetReportExpenseColumns } from '../components/FleetReportExpenseTable/excelUtils'
import AccountInfo from '../components/FleetReportDetailsTable/AccountInfo'
import { fetchFleetExpenseReport } from '../../../../api/newApi'
import getLastDateInMonth from '../../../../utils/getLastDateInMonth'

const FleetReportExpense = () => {
  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') || 'month'
  const { account, itemNo } = useParams<{ account: string; itemNo: string }>()

  const user = useUser()
  const { chosenCompany: company, fleetReportTerm: term, consolidateInvoice } = useUserContext() as any

  const consolidateCode = consolidateInvoice?.ConsolidateCode || '_ALL_'
  const year = new Date(term).getFullYear()
  const month = new Date(term).getMonth() + 1
  const lastDayOfMonth = getLastDateInMonth(term)

  const fromDate =
    type === 'year' ? `${year}-01-01T00:00:00Z` : `${year}-${String(month).padStart(2, '0')}-01T00:00:00Z`
  const toDate = type === 'year' ? `${year}-12-31T23:59:59Z` : `${lastDayOfMonth}T23:59:59Z`

  const companyId = company?.id
  const contactId = user?.user?.id

  const { data: car } = useQuery(
    ['carByAccountNo', contactId, companyId, account],
    () => fetchCarByAccountNo(contactId, companyId, account),
    { enabled: !!contactId && !!companyId && !!account },
  ) as { data: Car }

  const { data: reportResponse } = useQuery(
    ['fleetReportExpense', companyId, account, itemNo, term, type, consolidateCode],
    () =>
      fetchFleetExpenseReport({
        companyId,
        contactId,
        accountNo: account,
        itemNo,
        fromDate,
        toDate,
        consolidatedAgreementID: consolidateCode,
      }),
    { enabled: !!companyId && !!account && !!itemNo && !!term },
  )

  const reportData = (
    reportResponse && 'items' in reportResponse ? reportResponse.items : []
  ) as FleetReportExpenseType[]

  const columns = excelFleetReportExpenseColumns()
  const config = {
    columns,
    filename: `Flåteutgifter_${account}_${itemNo}_${type === 'year' ? year : month}.xlsx`,
    sheetName: `Flåteutgifter_${account}`,
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col gap-2">
            <span className="text-xl font-medium text-primaryText">Flåteutgifter</span>
            <AccountInfo car={car} />
          </div>
          <div className="md:hidden">
            <ExcelExportButton data={reportData} config={config} />
          </div>
        </div>
        <div className="flex flex-row gap-2 w-full md:w-auto md:self-end">
          <div className="hidden md:block">
            <ExcelExportButton data={reportData} config={config} />
          </div>
        </div>
      </div>
      <FleetReportExpenseTable data={reportData} />
    </div>
  )
}

export default FleetReportExpense
